import { calculateStandingsWithRanks, getPlayerReward, } from "./utils/tournamentUtils";
export class TournamentManager {
    repository;
    createPairingService;
    playerService;
    roundService;
    gameService;
    scheduleService;
    constructor(repository, createPairingService, playerService, roundService, gameService, scheduleService) {
        this.repository = repository;
        this.createPairingService = createPairingService;
        this.playerService = playerService;
        this.roundService = roundService;
        this.gameService = gameService;
        this.scheduleService = scheduleService;
    }
    async createTournament(tournamentData) {
        // Create tournament and emit TOURNAMENT_CREATED
        const tournamentId = await this.repository.createTournament(tournamentData);
        await this.scheduleRounds(tournamentId);
        return tournamentId;
    }
    async updateTournament(tournamentId, tournamentData) {
        await this.repository.updateTournament(tournamentId, tournamentData);
    }
    async deleteTournament(tournamentId) {
        await this.repository.deleteTournament(tournamentId);
    }
    async startTournament(tournamentId) {
        // Start tournament and emit TOURNAMENT_STARTED
        await this.repository.updateCurrentRound(tournamentId, 1);
    }
    async updatePlayerCount(tournamentId) {
        const players = await this.repository.getPlayers(tournamentId);
        await this.repository.updatePlayerCount(tournamentId, players.length);
    }
    async updatePlayerUsernames(tournamentId) {
        const players = await this.repository.getPlayers(tournamentId);
        await this.repository.updatePlayerUsernames(tournamentId, players.map((p) => p.username));
    }
    async updateStartTime(tournamentId, date) {
        await this.repository.updateStartTime(tournamentId, date);
    }
    async broadcastMessage(tournamentId, message) {
        await this.repository.broadcastMessage(tournamentId, message);
    }
    async checkTournamentCompletion(tournamentId) {
        const tournament = await this.repository.getTournament(tournamentId);
        if (tournament.currentRound >= tournament.totalRounds) {
            await this.finishTournament(tournamentId);
        }
    }
    async finishTournament(tournamentId) {
        // Finish tournament and emit TOURNAMENT_FINISHED
        await this.repository.finishTournament(tournamentId);
        await this.rewardPlayers(tournamentId);
    }
    async rewardPlayers(tournamentId) {
        const [tournament, rounds, players] = await Promise.all([
            this.repository.getTournament(tournamentId),
            this.repository.getRounds(tournamentId),
            this.repository.getPlayers(tournamentId),
        ]);
        const lastRound = rounds.find((r) => r.roundNumber === tournament.totalRounds);
        if (!lastRound?.standingsAfter) {
            return;
        }
        // Transform standings to match the RankedPlayer interface
        const standingsForRanking = lastRound.standingsAfter.map((standing) => ({
            score: standing.wins,
            tiebreaker: standing.tiebreaker,
            seed: standing.player.value,
            matches: standing.matches,
        }));
        const rankedStandings = calculateStandingsWithRanks(standingsForRanking);
        await Promise.all(rankedStandings.map(async (standing, index) => {
            // Find player using the original standings since we need player info
            const originalStanding = lastRound.standingsAfter[index];
            const player = players.find((p) => p.username === originalStanding.player.id);
            if (!player)
                return;
            const reward = getPlayerReward(standing.rank, tournament.reward, tournament.rewardParticipation);
            return this.repository.updatePlayer(tournamentId, player.username, {
                reward,
                score: standing.score,
                currentRank: standing.rank,
            });
        }));
    }
    async updateRoundStartTime(tournamentId, roundNumber, date) {
        const [tournament, rounds] = await Promise.all([
            this.repository.getTournament(tournamentId),
            this.repository.getRounds(tournamentId),
        ]);
        await this.scheduleService.setRoundStartTime(tournament, rounds, roundNumber, date);
    }
    async addPlayer(tournamentId, player) {
        await this.playerService.addPlayer(tournamentId, player);
    }
    /**
     * Add multiple players in a single batch operation for better performance
     */
    async addPlayers(tournamentId, players, participatingPlayerIds) {
        await this.playerService.addPlayers(tournamentId, players, participatingPlayerIds);
    }
    async removePlayer(tournamentId, username) {
        await this.playerService.removePlayer(tournamentId, username);
    }
    async participate(tournamentId, username, roundNumber = null, user = null) {
        await this.playerService.participate(tournamentId, username, roundNumber, user);
    }
    async unparticipate(tournamentId, username, roundNumber = null) {
        await this.playerService.unparticipate(tournamentId, username, roundNumber);
    }
    async scheduleRounds(tournamentId) {
        const [tournament, rounds] = await Promise.all([
            this.repository.getTournament(tournamentId),
            this.repository.getRounds(tournamentId),
        ]);
        await this.scheduleService.scheduleRounds(tournament, rounds);
    }
    async pushRounds(tournamentId, roundNumber, minutes) {
        const [tournament, rounds] = await Promise.all([
            this.repository.getTournament(tournamentId),
            this.repository.getRounds(tournamentId),
        ]);
        await this.scheduleService.pushRounds(tournament, rounds, roundNumber, minutes);
    }
    async pairRound(tournamentId, roundNumber) {
        const [tournament, round, players, games] = await Promise.all([
            this.repository.getTournament(tournamentId),
            this.repository.getRound(tournamentId, roundNumber),
            this.repository.getPlayers(tournamentId),
            this.repository.getGames(tournamentId),
        ]);
        const pairingService = this.createPairingService(tournament);
        const pairings = await pairingService.generatePairings(tournament, round, players, games);
        console.log("pairRound pairings", pairings);
        // Check if no valid pairings were generated
        if (!pairings || pairings.length === 0) {
            throw new Error("No possible pairings found");
        }
        await this.roundService.pairRound(tournamentId, roundNumber, pairings);
    }
    async saveRoundGames(tournamentId, games) {
        await this.roundService.saveRoundGames(tournamentId, games);
    }
    async startRound(tournamentId, roundNumber) {
        await this.roundService.startRound(tournamentId, roundNumber);
    }
    async startUnstartedRoundGames(tournamentId, roundNumber) {
        await this.roundService.startUnstartedRoundGames(tournamentId, roundNumber);
    }
    async abortRound(tournamentId, roundNumber) {
        await this.roundService.abortRound(tournamentId, roundNumber);
    }
    async finishRound(tournamentId, roundNumber) {
        await this.roundService.finishRound(tournamentId, roundNumber);
    }
    async unfinishRound(tournamentId, roundNumber) {
        await this.roundService.unfinishRound(tournamentId, roundNumber);
    }
    async updateStandings(tournamentId, roundNumber) {
        await this.roundService.updateStandings(tournamentId, roundNumber);
    }
    async fixRound(tournamentId, roundNumber) {
        await this.roundService.fixMismatchedGames(tournamentId, roundNumber);
    }
}
